import type React from "react";
import { Trans } from "react-i18next";
import {
  Flex,
  Heading,
  Image,
  Text,
  useBreakpointValue
} from "@chakra-ui/react";
import { Button, type ButtonProps } from "../Button";

export interface ErrorPageProps {
  bgColor?: string;
  bgPosition?: "left" | "right" | "full";
  statusCode: number;
  statusTitle?: string;
  statusDescription?: string;
  overrideImage?: string;
  btnPrimaryText?: string;
  btnPrimaryProps?: ButtonProps;
  onClickBtnPrimary?: () => void;
  btnSecondaryText?: string;
  btnSecondaryProps?: ButtonProps;
  onClickBtnSecondary?: () => void;
  isLessonError?: boolean;
}

import Image404 from "../../assets/img/status/404.svg";
import Image500 from "../../assets/img/status/500.svg";
import ImageDefault from "../../assets/img/status/503.svg";

const StatusImage = ({
  overrideImage,
  statusCode
}: {
  overrideImage?: string;
  statusCode?: number;
}) => {
  let image = ImageDefault;

  if (overrideImage !== undefined) {
    image = overrideImage;
  } else if (statusCode === 404) {
    image = Image404;
  } else if (statusCode === 500) {
    image = Image500;
  }

  return <Image src={image} data-testid="error-page-image" />;
};

const BackgroundSide = ({
  children,
  bgColor
}: {
  children: React.ReactNode;
  bgColor: string;
}) => {
  return (
    <Flex
      bgColor={bgColor}
      w="full"
      flex="1"
      alignItems="center"
      justifyContent="center"
      data-testid="error-page-side-image-wrapper"
    >
      {children}
    </Flex>
  );
};

export const ErrorPage = ({
  bgColor,
  bgPosition = "left",
  statusCode,
  statusTitle,
  statusDescription,
  overrideImage,
  btnPrimaryText,
  btnPrimaryProps,
  onClickBtnPrimary,
  btnSecondaryText,
  btnSecondaryProps,
  onClickBtnSecondary,
  isLessonError = false
}: ErrorPageProps) => {
  const useVerticalLayout = useBreakpointValue({ base: true, md: false });

  const getBackgroundColor = () => {
    if (bgColor) {
      return bgColor;
    }

    switch (statusCode) {
      case 404:
        return "highlightLilac.500";
      case 500:
        return "success.500";
      case 503:
        return "warning.500";
      default:
        return "warning.500";
    }
  };

  const getImage = () => {
    return (
      <StatusImage overrideImage={overrideImage} statusCode={statusCode} />
    );
  };

  const getLayout = () => {
    if (useVerticalLayout) {
      return "column";
    }

    switch (bgPosition) {
      case "left":
        return "row";
      case "right":
        return "row-reverse";
      default:
        return "column";
    }
  };

  const isFull = bgPosition === "full";

  return (
    <Flex
      data-bark-component="ErrorPage"
      bgColor={isFull ? getBackgroundColor() : "gradeTheme.pageBg"}
      w="full"
      flex="1"
      flexDirection={getLayout()}
      data-testid="error-page-root"
      alignItems={isFull ? "center" : undefined}
      justifyContent={isFull ? "center" : undefined}
      minH="100vh"
    >
      {!useVerticalLayout && !isFull ? (
        <BackgroundSide bgColor={getBackgroundColor()}>
          {getImage()}
        </BackgroundSide>
      ) : undefined}

      <Flex
        flexDirection="column"
        justifyContent="center"
        alignItems={isFull ? "center" : "flex-start"}
        w="full"
        flex="1"
        gap={isLessonError ? 6 : "8"}
        p="8"
        data-testid="error-page-content-wrapper"
        maxW={isFull ? "640px" : "full"}
        mx={isFull ? "auto" : undefined}
        textAlign={isFull ? "center" : "left"}
      >
        {useVerticalLayout || isFull ? getImage() : undefined}

        <Flex flexDirection="column" gap={isLessonError ? 2 : 8}>
          {statusTitle ? (
            <Heading
              data-testid="error-page-title"
              textStyle={isLessonError ? "body.large" : "title.1"}
              fontWeight={isLessonError ? "semibold" : "bold"}
              color="text"
              w="full"
              textAlign={isFull ? "center" : "left"}
            >
              <Trans>{statusTitle}</Trans>
            </Heading>
          ) : undefined}

          {statusDescription ? (
            <Text
              data-testid="error-page-description"
              textStyle="body.large"
              color="text"
              w="full"
              textAlign={isFull ? "center" : "left"}
            >
              <Trans>{statusDescription}</Trans>
            </Text>
          ) : undefined}
        </Flex>

        {btnPrimaryText || btnSecondaryText ? (
          <Flex
            gap="6"
            mt="4"
            data-testid="error-page-button-wrapper"
            w="full"
            justifyContent={isFull ? "center" : "flex-start"}
          >
            {btnSecondaryText ? (
              <Button
                variant="primaryHollow"
                {...btnSecondaryProps}
                onClick={onClickBtnSecondary}
                data-testid="error-page-button-secondary"
              >
                <Trans>{btnSecondaryText}</Trans>
              </Button>
            ) : undefined}
            {btnPrimaryText ? (
              <Button
                variant="primary"
                {...btnPrimaryProps}
                onClick={onClickBtnPrimary}
                data-testid="error-page-button-primary"
              >
                <Trans>{btnPrimaryText}</Trans>
              </Button>
            ) : undefined}
          </Flex>
        ) : undefined}
      </Flex>
    </Flex>
  );
};

export default ErrorPage;
